import React, { useState } from "react";
import CompetitorAnalysis from "./CompetitorAnalysis";
import AppStoreScreenshots from "./AppStoreScreenshots";

// Our planned screenshots
const ourScreenshots = [
  {
    id: 1,
    title: "Recording + Live Transcription",
    subtitle: "Waveform, timer, and streaming transcript text visible — shows the app transcribes in real-time",
    tagline: "Record meetings. AI transcribes instantly.",
    status: "needs-capture",
    features: ["Live transcript text", "Speaker labels", "Waveform + timer"],
  },
  {
    id: 2,
    title: "AI Summary with Citations",
    subtitle: "Note detail view with sections, AI summary, clickable citations",
    tagline: "AI summarizes with source citations.",
    status: "needs-capture",
    features: ["Timestamp citations", "Section breakdown", "Key points"],
  },
  {
    id: 3,
    title: "Home Screen",
    subtitle: "Folders + root notes showing audio, doc, YouTube, text mix",
    tagline: "All your content in one place.",
    status: "needs-capture",
    features: ["Mixed note types", "Folder organization", "Date grouping"],
  },
  {
    id: 4,
    title: "Live Translation",
    subtitle: "Transcript with real-time translation displayed below each segment",
    tagline: "Transcribe in any language. Translate live.",
    status: "needs-capture",
    features: ["60+ languages", "Side-by-side translation", "Per-segment"],
  },
  {
    id: 5,
    title: "YouTube Word-by-Word Playback",
    subtitle: "YouTube note with transcript, per-word highlighting during playback, and AI summary",
    tagline: "Play any video. Follow along word by word.",
    status: "needs-capture",
    features: ["Per-word highlighting", "YouTube auto-transcript", "AI summary + quiz"],
  },
  {
    id: 6,
    title: "AI Chat with Context",
    subtitle: "Chat with citations referencing meeting content",
    tagline: "Ask anything about your meetings.",
    status: "needs-capture",
    features: ["RAG-powered", "Source citations", "Cross-note memory"],
  },
];

const internalPages = [
  { name: "Screenshots Generator", path: "/internal/screenshots", description: "iPhone/iPad frame generator for App Store assets" },
  { name: "Competitor Analysis", path: "/internal/competitors", description: "App Store screenshots & positioning from 6 competitors" },
  { name: "Landing Cards", path: "/internal/landing-cards", description: "Interactive feature demo cards for landing page" },
  { name: "Style Guide", path: "/internal/style-guide", description: "Design tokens, colors, typography reference" },
  { name: "Design Playground", path: "/internal/design-playground", description: "Component sandbox for prototyping" },
  { name: "Desktop Download", path: "/internal/download", description: "Desktop app download page" },
];

const positioning = {
  current: {
    title: "YouWo",
    subtitle: "AI Note Taker & Study Partner",
    description: "Record your lectures. Upload your docs. AI does the rest.",
    issue: "Student-focused. Doesn't reflect meeting/work positioning.",
  },
  proposed: {
    title: "YouWo: AI Meeting Notes & Docs",
    subtitle: "Record, Transcribe, Translate, Chat",
    description: "Record meetings. Import docs. Your AI agent takes it from here.",
    rationale: "Matches competitor keywords ('AI Meeting Notes') while differentiating with 'Docs' and 'Translate'.",
  },
};

type Tab = "dashboard" | "competitors" | "screenshots" | "generator";

export default function InternalDashboard() {
  const [tab, setTab] = useState<Tab>("dashboard");

  const searchParams = new URLSearchParams(window.location.search);
  const key = searchParams.get("key") || "";

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa", fontFamily: "Inter, -apple-system, sans-serif" }}>
      {/* Nav */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <h1 style={{ fontSize: 16, fontWeight: 700, padding: "16px 0", margin: 0 }}>YouWo Internal</h1>
            {(["dashboard", "competitors", "screenshots", "generator"] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: "16px 0",
                  fontSize: 13,
                  fontWeight: tab === t ? 600 : 400,
                  color: tab === t ? "#111" : "#6b7280",
                  background: "none",
                  border: "none",
                  borderBottom: tab === t ? "2px solid #111" : "2px solid transparent",
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {t === "screenshots" ? "Our Screenshots" : t === "generator" ? "Frame Generator" : t}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>Internal only</span>
        </div>
      </div>

      {/* Content */}
      {tab === "dashboard" && (
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
          {/* Positioning */}
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>App Store Positioning</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 40 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #fecaca" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#dc2626", marginBottom: 8, textTransform: "uppercase" }}>Current (Outdated)</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>{positioning.current.title}</h3>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 8px" }}>{positioning.current.subtitle}</p>
              <p style={{ fontSize: 13, margin: "0 0 12px" }}>{positioning.current.description}</p>
              <p style={{ fontSize: 12, color: "#dc2626", fontStyle: "italic", margin: 0 }}>{positioning.current.issue}</p>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #bbf7d0" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#16a34a", marginBottom: 8, textTransform: "uppercase" }}>Proposed</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>{positioning.proposed.title}</h3>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 8px" }}>{positioning.proposed.subtitle}</p>
              <p style={{ fontSize: 13, margin: "0 0 12px" }}>{positioning.proposed.description}</p>
              <p style={{ fontSize: 12, color: "#16a34a", fontStyle: "italic", margin: 0 }}>{positioning.proposed.rationale}</p>
            </div>
          </div>

          {/* Screenshot Plan */}
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Screenshot Plan (6 screens)</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 40 }}>
            {ourScreenshots.map((s) => (
              <div key={s.id} style={{ background: "#fff", borderRadius: 12, padding: 16, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ fontSize: 24, fontWeight: 700, color: "#d1d5db" }}>#{s.id}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                    background: s.status === "captured" ? "#dcfce7" : "#fef3c7",
                    color: s.status === "captured" ? "#16a34a" : "#d97706",
                  }}>
                    {s.status === "captured" ? "CAPTURED" : "NEEDS CAPTURE"}
                  </span>
                </div>
                <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>{s.title}</h3>
                <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 8px" }}>{s.subtitle}</p>
                <p style={{ fontSize: 12, color: "#3b82f6", fontStyle: "italic", margin: "0 0 12px" }}>"{s.tagline}"</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {s.features.map((f) => (
                    <span key={f} style={{ fontSize: 10, background: "#f3f4f6", color: "#374151", padding: "2px 6px", borderRadius: 4 }}>{f}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Demo Account Info */}
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Demo Accounts</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 40 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 16, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>ENGLISH</div>
              <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>demo-en@youwo.ai</p>
              <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>Password: demo123 &middot; Internal tier &middot; 4 folders, 20 notes, 5 transcript parts</p>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 16, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>CHINESE</div>
              <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>demo-zh@youwo.ai</p>
              <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>Password: demo123 &middot; Internal tier &middot; 4 folders, 20 notes, 5 transcript parts</p>
            </div>
          </div>

          {/* Quick Links */}
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Internal Tools</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {internalPages.map((p) => (
              <a
                key={p.path}
                href={`${p.path}?key=${key}`}
                style={{
                  background: "#fff", borderRadius: 10, padding: 16, border: "1px solid #e5e7eb",
                  textDecoration: "none", color: "inherit", display: "block",
                }}
              >
                <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>{p.name}</h3>
                <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>{p.description}</p>
              </a>
            ))}
          </div>
        </div>
      )}

      {tab === "competitors" && <CompetitorAnalysis />}

      {tab === "screenshots" && (
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Our App Store Screenshots</h2>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>
            Planned screenshots for Apple App Store. Take raw screenshots on device, then use the{" "}
            <a href={`/internal/screenshots?key=${key}`} style={{ color: "#3b82f6" }}>Screenshots Generator</a>{" "}
            to add iPhone frames and text overlays.
          </p>

          {ourScreenshots.map((s) => (
            <div key={s.id} style={{
              background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #e5e7eb",
              marginBottom: 16, display: "flex", gap: 20,
            }}>
              {/* Placeholder for screenshot image */}
              <div style={{
                width: 120, height: 260, background: "#f3f4f6", borderRadius: 10, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#9ca3af", fontSize: 12, textAlign: "center", padding: 8,
              }}>
                {s.status === "captured" ? "✓ Captured" : `Screenshot #${s.id}`}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 4px" }}>#{s.id} — {s.title}</h3>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                    background: s.status === "captured" ? "#dcfce7" : "#fef3c7",
                    color: s.status === "captured" ? "#16a34a" : "#d97706",
                  }}>
                    {s.status === "captured" ? "CAPTURED" : "NEEDS CAPTURE"}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 8px" }}>{s.subtitle}</p>
                <p style={{ fontSize: 14, color: "#3b82f6", fontWeight: 500, margin: "0 0 12px" }}>"{s.tagline}"</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {s.features.map((f) => (
                    <span key={f} style={{ fontSize: 11, background: "#eff6ff", color: "#1d4ed8", padding: "3px 8px", borderRadius: 4 }}>{f}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "generator" && <AppStoreScreenshots />}
    </div>
  );
}
